import { tryConvertToValidStructuredCommunication } from "../components/Tools/Validator";
import { DecodeQuantity, DecodeAmount, DecodeDoc, AdditionalDocumentReference, Line, TaxSubtotal, PeppolDocumentType, PeppolXmlLevel } from "./PeppolInterfaces";

function readQuantity(node: Element): DecodeQuantity | undefined {
    const text = node.textContent?.trim() ?? "";
    const unitCode = node.getAttribute("unitCode") ?? "";

    if (!text || isNaN(Number(text))) return undefined;
    return { amount: Number(text), unitCode };
}
function readNumber(node: Element): number | undefined {
    if (!node || !node.textContent) return undefined;
    const text = node.textContent?.trim() ?? "";

    if (!text || isNaN(Number(text))) return undefined;
    return Number(text);
}

function readAmount2(node: Element): DecodeAmount | undefined {
    if (!node) return undefined;
    const text = node.textContent?.trim() ?? "";
    const currency = node.getAttribute("currencyID") ?? "";

    if (!text || isNaN(Number(text))) return undefined;
    return { amount: Number(text), currency };
}

function getValue(doc: Document, xpath: string, nsResolver: XPathNSResolver): string | undefined {
    const node = doc.evaluate(xpath, doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
    return node?.textContent?.trim() || undefined;
}

function base64ToBlob(base64: string, mimeType = "application/octet-stream") {
    // Remove any data URL prefix if present
    const cleaned = base64.replace(/^data:[^;]+;base64,/, "");

    // Decode base64 to raw binary string
    const byteChars = atob(cleaned);

    // Convert binary string to array of bytes
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // Create a Blob from the byte array
    return new Blob([byteArray], { type: mimeType });
}

export function AnalysePeppolXML(s: string): DecodeDoc {
    const parser = new DOMParser();
    const doc = parser.parseFromString(s, "text/xml");

    // Check for parsing errors
    if (doc.getElementsByTagName("parsererror").length > 0) {
        console.log("Invalid XML");
        throw new Error("Invalid XML");
    }

    // Namespace URIs for PEPPOL UBL
    const nsResolver = ((prefix: string) => {
        switch (prefix) {
            case "cbc":
                return "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2";
            case "cac":
                return "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2";
            case "inv":
                return "urn:oasis:names:specification:ubl:schema:xsd:Invoice-2";
            case "cn":
                return "urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2";
            default:
                return null;
        }
    }) as XPathNSResolver;

    const decodeDoc: DecodeDoc = {
        additionalDocumentReferences: [],
        lines: [],
        taxSubtotals: [],
    };

    decodeDoc.ID = doc.evaluate("//cbc:ID", doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue?.textContent?.trim() || undefined;
    decodeDoc.supplierName = doc.evaluate("//cac:AccountingSupplierParty//cbc:Name", doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue?.textContent?.trim() || "";
    if (decodeDoc.supplierName === "") {
        decodeDoc.supplierName = doc.evaluate("//cac:AccountingSupplierParty//cac:Party//cac:PartyLegalEntity//cbc:RegistrationName", doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue?.textContent?.trim() || "";
    }
    decodeDoc.customerName = doc.evaluate("//cac:AccountingCustomerParty//cbc:Name", doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue?.textContent?.trim() || "";
    if (decodeDoc.customerName === "") {
        decodeDoc.customerName = doc.evaluate("//cac:AccountingCustomerParty//cac:Party//cac:PartyLegalEntity//cbc:RegistrationName", doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue?.textContent?.trim() || "";
    }
    const issueDate = doc.evaluate("//cbc:IssueDate", doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue?.textContent?.trim();
    if (issueDate) {
        decodeDoc.issueDate = new Date(issueDate);
    }
    const dueDate = doc.evaluate("//cbc:DueDate", doc, nsResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue?.textContent?.trim();
    if (dueDate) {
        decodeDoc.dueDate = new Date(dueDate);
    }
    decodeDoc.taxExclusive = readAmount2(doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
        "TaxExclusiveAmount"
    )[0]);
    decodeDoc.taxInclusive = readAmount2(doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
        "TaxInclusiveAmount"
    )[0]);
    decodeDoc.payable = readAmount2(doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
        "PayableAmount"
    )[0]);
    decodeDoc.taxAmount = readAmount2(doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
        "TaxAmount"
    )[0]);
    decodeDoc.prepaidAmount = readAmount2(doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
        "PrepaidAmount"
    )[0]);


    const accountingCustomerParty = doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        "AccountingCustomerParty"
    );
    if (accountingCustomerParty && accountingCustomerParty.length > 0) {
        const accountingCustomerPartyEndpointID = accountingCustomerParty[0].getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "EndpointID"
        );
        if (accountingCustomerPartyEndpointID && accountingCustomerPartyEndpointID.length > 0) {

            decodeDoc.customerEndpointID = accountingCustomerPartyEndpointID[0]?.getAttribute("schemeID") + ":" + accountingCustomerPartyEndpointID[0]?.textContent;
        }
    }

    decodeDoc.communication = getValue(doc, "//cac:PaymentMeans//cbc:PaymentID", nsResolver);
    decodeDoc.communication = tryConvertToValidStructuredCommunication(decodeDoc.communication);
    decodeDoc.iban = getValue(doc, "//cac:PaymentMeans//cac:PayeeFinancialAccount//cbc:ID", nsResolver);
    decodeDoc.bic = getValue(doc, "//cac:PaymentMeans//cac:PayeeFinancialAccount//cac:FinancialInstitutionBranch//cbc:ID", nsResolver);
    decodeDoc.accountName = getValue(doc, "//cac:PaymentMeans//cac:PayeeFinancialAccount//cbc:Name", nsResolver);

    // Get all AdditionalDocumentReference elements
    const refs = doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        "AdditionalDocumentReference"
    );

    // Iterate and extract values of AdditionalDocumentReference elements
    for (let i = 0; i < refs.length; i++) {
        const ref = refs[i];
        const a: AdditionalDocumentReference = {
            ID: ref.getElementsByTagNameNS(
                "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
                "ID"
            )[0]?.textContent
        };
        a.documentDescription = ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "DocumentDescription"
        )[0]?.textContent;
        a.documentTypeCode = ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "DocumentTypeCode"
        )[0]?.textContent;
        const attachment = ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
            "Attachment"
        );
        if (attachment) {
            const embeddedDocumentBinaryObject = ref.getElementsByTagNameNS(
                "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
                "EmbeddedDocumentBinaryObject"
            )[0];
            const fileData = embeddedDocumentBinaryObject?.textContent;
            if (fileData) {
                a.mimeCode = embeddedDocumentBinaryObject?.getAttribute("mimeCode") ?? undefined;
                a.filename = embeddedDocumentBinaryObject?.getAttribute("filename") ?? undefined;
                a.blob = base64ToBlob(fileData, a.mimeCode);
                decodeDoc.additionalDocumentReferences.push(a);
            }
        }
    }

    // Get all TaxSubTotal
    const taxTotal = doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        "TaxTotal"
    );
    const taxSubtotal = taxTotal[0].getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        "TaxSubtotal"
    );
    // Iterate and extract values of TaxSubTotal line elements
    for (let i = 0; i < taxSubtotal.length; i++) {
        const ref = taxSubtotal[i];
        const a: TaxSubtotal = {
            ID: "S"
        };
        a.taxableAmount = readAmount2(ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "TaxableAmount"
        )[0]);
        a.taxAmount = readAmount2(ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "TaxAmount"
        )[0]);
        a.ID = ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "ID"
        )[0]?.textContent;
        a.percent = readNumber(ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "Percent"
        )[0]);

        decodeDoc.taxSubtotals.push(a);
    }



    // Get all invoice/credit note line elements
    let linesRefs = doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
        "InvoiceLine"
    );
    let isInvoice = true;
    if (linesRefs.length === 0) {
        linesRefs = doc.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
            "CreditNoteLine"
        );
        isInvoice = false;
    }
    // Iterate and extract values of invoice/credit note line elements
    for (let i = 0; i < linesRefs.length; i++) {
        const ref = linesRefs[i];
        const a: Line = {
            ID: ref.getElementsByTagNameNS(
                "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
                "ID"
            )[0]?.textContent
        };
        if (isInvoice) {
            a.quantity = readQuantity(ref.getElementsByTagNameNS(
                "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
                "InvoicedQuantity"
            )[0]);
        }
        else {
            a.quantity = readQuantity(ref.getElementsByTagNameNS(
                "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
                "CreditedQuantity"
            )[0]);
        }
        a.priceAmount = readAmount2(ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "PriceAmount"
        )[0]);
        a.lineExtensionAmount = readAmount2(ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "LineExtensionAmount"
        )[0]);
        a.name = ref.getElementsByTagNameNS(
            "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
            "Name"
        )[0]?.textContent;

        decodeDoc.lines.push(a);
    }



    return decodeDoc;
}

export function GetPeppolDocumentType(s: string): { type: PeppolDocumentType; level: PeppolXmlLevel } | undefined {
    const parser = new DOMParser();
    const doc = parser.parseFromString(s, "text/xml");
    
    // Check for parsing errors
    if (doc.getElementsByTagName("parsererror").length > 0) {
        console.log("Invalid XML");
        throw new Error("Invalid XML");
    }


    // if it has the SBD StandardBusinessDocumentHeader it's SDB, otherwise it's UBL
    const sbdh = doc.getElementsByTagNameNS(
        "urn:oasis:names:specification:ubl:schema:xsd:StandardBusinessDocumentHeader-2",
        "StandardBusinessDocumentHeader"
    )[0];
    const xmlLevel = sbdh ? PeppolXmlLevel.SDB : PeppolXmlLevel.UBL;

    const rootNodeName = doc.documentElement.localName;
    switch (rootNodeName) {
        case "Invoice":
            return { type: PeppolDocumentType.Invoice, level: xmlLevel };
        case "CreditNote":
            return { type: PeppolDocumentType.CreditNote, level: xmlLevel };
        case "InvoiceResponse":
            return { type: PeppolDocumentType.InvoiceResponse, level: xmlLevel };
        default:
            return undefined;
    }
}